import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { API_CONFIG } from '../config/api';
import type { AdminUser } from '../types';
import type { Membership, Permission } from '../types/permissions';
import { DEFAULT_ROLE_PERMISSIONS } from '../types/permissions';
import { devError } from '../utils/dev-log';

// ---------------------------------------------------------------------------
// Context type
// ---------------------------------------------------------------------------

interface AuthContextType {
  user: User | null;
  adminUser: AdminUser | null;
  loading: boolean;
  authError: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ---------------------------------------------------------------------------
// Role hierarchy for determining "primary" role (display purposes)
// ---------------------------------------------------------------------------

const ROLE_PRIORITY: Record<string, number> = {
  super_admin: 0,
  portfolio_owner: 1,
  org_admin: 2,
  regional_director: 3,
  general_manager: 4,
  marketing_manager: 5,
  revenue_manager: 5,
  concierge: 6,
  front_desk: 6,
  spa_manager: 7,
  fb_manager: 7,
  agent: 8,
};

function derivePrimaryRole(memberships: Membership[]): string {
  if (memberships.length === 0) return 'agent';

  let bestRole = memberships[0].roleId;
  let bestPriority = ROLE_PRIORITY[bestRole] ?? 99;

  for (const m of memberships) {
    const p = ROLE_PRIORITY[m.roleId] ?? 99;
    if (p < bestPriority) {
      bestPriority = p;
      bestRole = m.roleId;
    }
  }
  return bestRole;
}

// ---------------------------------------------------------------------------
// Backend session exchange
// ---------------------------------------------------------------------------

interface SessionPayload {
  success: boolean;
  user: {
    uid: string;
    email: string;
    displayName: string;
    status: string;
  };
  permissions: Permission[];
  memberships: Membership[];
  accessiblePropertyIds: string[];
  accessibleOrganizationIds: string[];
  has2FA?: boolean;
  needs2FASetup?: boolean;
  signInProvider?: string;
  activation?: {
    requiresActivation: boolean;
    organizationId?: string;
    propertyId?: string;
    activationStatus?: string;
    provisioningSource?: string;
    paymentStatus?: string;
    packageStatus?: string;
    nextRoute?: string;
  };
}

async function exchangeToken(idToken: string): Promise<SessionPayload> {
  const res = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.auth.session}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Session exchange failed (${res.status})`);
  }

  return res.json();
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  // Tracks whether signIn() already called loadSession — prevents the
  // onAuthStateChanged listener from calling it a second time.
  const signInLoadedRef = useRef(false);

  // Build AdminUser from session payload
  const buildAdminUser = useCallback(
    (firebaseUser: User, session: SessionPayload): AdminUser => {
      const primaryRole = derivePrimaryRole(session.memberships);
      const isSuperAdmin = session.permissions.includes('system:full_access');

      return {
        uid: session.user.uid,
        email: session.user.email,
        displayName: session.user.displayName || firebaseUser.displayName || undefined,
        status: session.user.status as AdminUser['status'],
        has2FA: session.has2FA ?? false,
        needs2FASetup: session.needs2FASetup ?? false,
        signInProvider: session.signInProvider,

        permissions: session.permissions,
        memberships: session.memberships,
        accessiblePropertyIds: session.accessiblePropertyIds,
        accessibleOrganizationIds: session.accessibleOrganizationIds,

        primaryRole,
        isSuperAdmin,

        // Activation state (sales-assisted provisioning flow)
        activation: session.activation,

        // Legacy fields
        role: primaryRole,
        organizationId: session.accessibleOrganizationIds[0],
        propertyIds: session.accessiblePropertyIds,
        roleIds: [...new Set(session.memberships.map((m) => m.roleId))],
        createdAt: new Date(),
        lastLogin: new Date(),
      };
    },
    [],
  );

  // Load session from backend using the Firebase ID token
  const loadSession = useCallback(
    async (firebaseUser: User) => {
      try {
        const idToken = await firebaseUser.getIdToken();
        const session = await exchangeToken(idToken);

        if (session.success) {
          // Force refresh to pick up custom claims set by backend during session exchange
          await firebaseUser.getIdToken(true);

          const admin = buildAdminUser(firebaseUser, session);
          setAdminUser(admin);
          setAuthError(false);
          return;
        }

        throw new Error('Session exchange returned success=false');
      } catch (error) {
        devError('Session exchange failed, falling back to dev mode:', error);

        // In dev mode with SKIP_AUTH, this is already handled separately
        // Otherwise, the user isn't set up in the new system yet
        setAdminUser(null);
        setAuthError(true);
      }
    },
    [buildAdminUser],
  );

  useEffect(() => {
    // Dev skip-auth mode
    if (import.meta.env.DEV && import.meta.env.VITE_SKIP_AUTH === 'true') {
      const devUser = { uid: 'dev-user', email: 'dev@test.com' } as User;
      setUser(devUser);
      setAdminUser({
        uid: 'dev-user',
        email: 'dev@test.com',
        displayName: 'Dev User',
        status: 'active',
        has2FA: false,
        needs2FASetup: false,
        permissions: [...DEFAULT_ROLE_PERMISSIONS.super_admin],
        memberships: [],
        accessiblePropertyIds: ['demo-property'],
        accessibleOrganizationIds: [],
        primaryRole: 'super_admin',
        isSuperAdmin: true,
        role: 'super_admin',
        organizationId: undefined,
        propertyIds: ['demo-property'],
        roleIds: ['super_admin'],
        createdAt: new Date(),
        lastLogin: new Date(),
      });
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        // Skip if signIn() already loaded the session for this auth change
        if (signInLoadedRef.current) {
          signInLoadedRef.current = false;
        } else {
          await loadSession(firebaseUser);
        }
      } else {
        setUser(null);
        setAdminUser(null);
        setAuthError(false);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [loadSession]);

  // Shared sign-in flow: mark ref so onAuthStateChanged skips duplicate loadSession.
  // Set ref AFTER auth succeeds to avoid leaking the flag on popup failures.
  async function signInAndLoad(getCredential: () => Promise<{ user: User }>) {
    const credential = await getCredential();
    signInLoadedRef.current = true;
    await loadSession(credential.user);
  }

  const signIn = (email: string, password: string) =>
    signInAndLoad(() => signInWithEmailAndPassword(auth, email, password));

  const signInWithGoogle = () =>
    signInAndLoad(() => signInWithPopup(auth, new GoogleAuthProvider()));

  const signInWithMicrosoft = () => {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({ prompt: 'select_account' });
    return signInAndLoad(() => signInWithPopup(auth, provider));
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const refreshSession = useCallback(async () => {
    if (user) {
      await loadSession(user);
    }
  }, [user, loadSession]);

  // Periodic session refresh — detect membership changes server-side
  useEffect(() => {
    if (!user) return;
    const REFRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
    const id = setInterval(() => {
      refreshSession();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps -- only re-run when user changes, not on every refreshSession identity change

  // Refresh session when tab regains focus after being hidden for >5 minutes
  useEffect(() => {
    if (!user) return;
    let hiddenAt: number | null = null;
    const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt > STALE_THRESHOLD_MS) {
        refreshSession();
        hiddenAt = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps -- only re-run when user changes

  const value: AuthContextType = {
    user,
    adminUser,
    loading,
    authError,
    signIn,
    signInWithGoogle,
    signInWithMicrosoft,
    signOut,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
